/**
 * @cfleet/websocket - WebSocketProvider
 *
 * Provides WebSocket connection management for CFleet applications.
 * Handles connection lifecycle, reconnection with backoff, and topic-based subscriptions.
 *
 * Connects to the optimization-processor service for real-time updates.
 *
 * Message format matches backend exactly:
 * - Outbound: { type: 'Subscribe', payload: { topic: '...' } }
 * - Inbound: { type: 'JobProgress', payload: { ... } }
 *
 * See: services/optimization-processor/src/ws/messages.rs
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
  type ReactNode,
} from 'react';
import type {
  WebSocketContextValue,
  ConnectionStatus,
  WebSocketConfig,
  MessageHandler,
  WsOutMessage,
  WsInMessage,
} from './types';

const WebSocketContext = createContext<WebSocketContextValue | null>(null);

/**
 * Check if a JWT token is expired (or about to expire within 30s).
 * Parses the payload without verifying the signature.
 */
function isTokenExpired(token: string): boolean {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return true;
    const payload = JSON.parse(atob(parts[1]));
    if (typeof payload.exp !== 'number') return false;
    // Consider expired if within 30 seconds of expiry
    return Date.now() / 1000 >= payload.exp - 30;
  } catch {
    return true;
  }
}

/**
 * Props for WebSocketProvider component
 */
export interface WebSocketProviderProps {
  children: ReactNode;
  /** WebSocket configuration */
  config?: Partial<WebSocketConfig>;
  /** Called when connection status changes */
  onStatusChange?: (status: ConnectionStatus) => void;
  /** Called when an error occurs */
  onError?: (error: Error) => void;
}

/**
 * Default WebSocket configuration
 */
const DEFAULT_CONFIG: WebSocketConfig = {
  url: 'ws://localhost:8443/ws', // Through Envoy gateway
  reconnect: {
    enabled: true,
    maxAttempts: 10,
    initialDelay: 1000,
    maxDelay: 30000,
    backoffMultiplier: 1.5,
  },
  pingInterval: 30000,
  connectionTimeout: 10000,
  getAuthToken: () => null,
};

/**
 * WebSocketProvider component
 *
 * Manages WebSocket connection and provides context for subscribing to topics.
 *
 * @example
 * ```tsx
 * import { WebSocketProvider } from '@cfleet/websocket';
 * import { useAuth } from '@cfleet/auth';
 *
 * function App() {
 *   const { getAccessToken } = useAuth();
 *
 *   return (
 *     <WebSocketProvider
 *       config={{
 *         url: 'ws://localhost:8443/ws',  // Through Envoy gateway
 *         getAuthToken: getAccessToken,
 *       }}
 *     >
 *       <MyApp />
 *     </WebSocketProvider>
 *   );
 * }
 * ```
 */
export function WebSocketProvider({
  children,
  config: configOverrides,
  onStatusChange,
  onError,
}: WebSocketProviderProps) {
  const config = useMemo(
    () => ({
      ...DEFAULT_CONFIG,
      ...configOverrides,
      reconnect: {
        ...DEFAULT_CONFIG.reconnect,
        ...configOverrides?.reconnect,
      },
    }),
    [configOverrides]
  );

  // Ref for config so connect/startHeartbeat/getReconnectDelay are stable
  const configRef = useRef(config);
  configRef.current = config;

  // Refs for callbacks to avoid cascading useCallback instability
  const onStatusChangeRef = useRef(onStatusChange);
  onStatusChangeRef.current = onStatusChange;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  const [status, setStatus] = useState<ConnectionStatus>('disconnected');
  const statusRef = useRef<ConnectionStatus>('disconnected');
  const [lastError, setLastError] = useState<Error | null>(null);

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const heartbeatIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const subscriptionsRef = useRef<Map<string, Set<MessageHandler>>>(new Map());
  const subscribedTopicsRef = useRef<Set<string>>(new Set());

  /**
   * Update connection status and notify listeners
   */
  const updateStatus = useCallback((newStatus: ConnectionStatus) => {
    statusRef.current = newStatus;
    setStatus(newStatus);
    onStatusChangeRef.current?.(newStatus);
  }, []);

  /**
   * Handle WebSocket errors
   */
  const handleError = useCallback((error: Error) => {
    setLastError(error);
    onErrorRef.current?.(error);
  }, []);

  /**
   * Send a raw message through the WebSocket
   */
  const send = useCallback(<T = unknown,>(message: T): void => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
    }
  }, []);

  /**
   * Send a typed inbound message (client → server)
   */
  const sendInMessage = useCallback(
    (message: WsInMessage): void => {
      send(message);
    },
    [send]
  );

  /**
   * Subscribe to a topic on the server
   * Uses backend format: { type: 'Subscribe', payload: { topic: '...' } }
   */
  const subscribeToTopic = useCallback(
    (topic: string) => {
      sendInMessage({ type: 'Subscribe', payload: { topic } });
      subscribedTopicsRef.current.add(topic);
    },
    [sendInMessage]
  );

  /**
   * Unsubscribe from a topic on the server
   * Uses backend format: { type: 'Unsubscribe', payload: { topic: '...' } }
   */
  const unsubscribeFromTopic = useCallback(
    (topic: string) => {
      sendInMessage({ type: 'Unsubscribe', payload: { topic } });
      subscribedTopicsRef.current.delete(topic);
    },
    [sendInMessage]
  );

  /**
   * Start heartbeat to keep connection alive
   * Uses backend format: { type: 'Ping' }
   */
  const startHeartbeat = useCallback(() => {
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
    }

    heartbeatIntervalRef.current = setInterval(() => {
      sendInMessage({ type: 'Ping' });
    }, configRef.current.pingInterval);
  }, [sendInMessage]);

  /**
   * Stop heartbeat
   */
  const stopHeartbeat = useCallback(() => {
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }
  }, []);

  /**
   * Calculate reconnect delay with exponential backoff
   */
  const getReconnectDelay = useCallback(() => {
    const {
      initialDelay = 1000,
      backoffMultiplier = 1.5,
      maxDelay = 30000,
    } = configRef.current.reconnect || {};
    const delay = initialDelay * Math.pow(backoffMultiplier, reconnectAttemptsRef.current);
    return Math.min(delay, maxDelay);
  }, []);

  /**
   * Route an incoming message to the appropriate handlers
   */
  const routeMessage = useCallback((message: WsOutMessage) => {
    // Extract topic from message if present
    // The backend sends messages with a type and payload, but may also include
    // the topic for context in some cases

    // Notify all global subscribers (subscribed to '*')
    const globalHandlers = subscriptionsRef.current.get('*');
    globalHandlers?.forEach((handler) => {
      try {
        handler(message);
      } catch (err) {
        console.error('Error in global message handler:', err);
      }
    });

    // For topic-specific routing, we need to infer the topic from the message
    // Job-related messages have job_id in payload - route to org-scoped job topics
    if (message.type === 'JobProgress' || message.type === 'RouteSummary') {
      // Route to all org-scoped job topics that match this job_id
      // Topic format: {org_id}:job:{job_id}
      subscriptionsRef.current.forEach((handlers, topic) => {
        if (topic.includes(':job:') && topic.includes(message.payload.job_id)) {
          handlers.forEach((handler) => {
            try {
              handler(message);
            } catch (err) {
              console.error(`Error in handler for topic ${topic}:`, err);
            }
          });
        }
      });
    }

    // Dispatch updates have route_id
    if (message.type === 'DispatchUpdate' || message.type === 'StopUpdate') {
      // These could be routed to org:dispatch:route_id topic
      // For now, notify all dispatch subscribers
      subscriptionsRef.current.forEach((handlers, topic) => {
        if (topic.includes(':dispatch:') && topic.includes(message.payload.route_id)) {
          handlers.forEach((handler) => {
            try {
              handler(message);
            } catch (err) {
              console.error(`Error in handler for topic ${topic}:`, err);
            }
          });
        }
      });
    }

    // Driver location updates have driver_id - route to org-scoped driver topics only
    if (message.type === 'DriverLocation') {
      const driverId = message.payload.driver_id;
      // Only route to org-scoped topics (no legacy topic support)
      // Topic format: {org_id}:driver:{driver_id}
      subscriptionsRef.current.forEach((handlers, topic) => {
        if (topic.includes(':driver:') && topic.includes(driverId)) {
          handlers.forEach((handler) => {
            try {
              handler(message);
            } catch (err) {
              console.error(`Error in handler for topic ${topic}:`, err);
            }
          });
        }
      });
    }

    // Chat messages and messages read have driver_id
    if (message.type === 'ChatMessage' || message.type === 'MessagesRead') {
      const driverId = message.payload.driver_id;

      subscriptionsRef.current.forEach((handlers, topic) => {
        // Route to driver-specific chat topics ({orgId}:chat:{driverId})
        // AND org-wide dispatch-chat topics ({orgId}:dispatch-chat)
        const isDriverChat = topic.includes(':chat:') && topic.includes(driverId);
        const isDispatchChat = topic.endsWith(':dispatch-chat');
        if (isDriverChat || isDispatchChat) {
          handlers.forEach((handler) => {
            try {
              handler(message);
            } catch (err) {
              console.error(`Error in handler for topic ${topic}:`, err);
            }
          });
        }
      });
    }

    // Alert notifications go to org alert topics
    if (message.type === 'Alert') {
      subscriptionsRef.current.forEach((handlers, topic) => {
        if (topic.endsWith(':alert')) {
          handlers.forEach((handler) => {
            try {
              handler(message);
            } catch (err) {
              console.error(`Error in handler for topic ${topic}:`, err);
            }
          });
        }
      });
    }
  }, []);

  /**
   * Connect to WebSocket server
   */
  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      return;
    }

    // Build URL with auth token if available
    const getAuthToken = configRef.current.getAuthToken;
    const hasAuthConfigured = getAuthToken !== DEFAULT_CONFIG.getAuthToken;
    const token = getAuthToken?.();

    // If auth is configured but token isn't available or is expired, schedule a retry.
    // This handles the case where the JWT has expired and the auth layer
    // hasn't yet refreshed it — without this, the reconnect loop dies silently
    // or keeps connecting with an expired token (getting 401s from the backend).
    if (hasAuthConfigured && (!token || isTokenExpired(token))) {
      if (token) {
        console.warn('[WS] JWT token is expired, waiting for refresh before connecting');
      }
      updateStatus('disconnected');
      const retryDelay = 2000;
      reconnectTimeoutRef.current = setTimeout(() => {
        connect();
      }, retryDelay);
      return;
    }

    // Clear any pending reconnect
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    updateStatus('connecting');

    try {
      let wsUrl = configRef.current.url;
      if (token) {
        // Resolve relative URLs (e.g. "/ws") against the page origin
        // so staging builds with VITE_WS_URL=/ws work correctly.
        // Absolute URLs (ws://... or wss://...) ignore the base.
        const base =
          typeof window !== 'undefined' && window.location?.origin
            ? window.location.origin.replace(/^http/, 'ws')
            : undefined;
        const url = new URL(wsUrl, base);
        url.searchParams.set('token', token);
        wsUrl = url.toString();
      }

      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        updateStatus('connected');
        reconnectAttemptsRef.current = 0;
        setLastError(null);
        startHeartbeat();

        // Resubscribe to any active subscriptions
        subscriptionsRef.current.forEach((_, topic) => {
          // Don't send subscribe for global topic
          if (topic !== '*') {
            subscribeToTopic(topic);
          }
        });
      };

      ws.onclose = (event) => {
        stopHeartbeat();

        if (event.wasClean) {
          console.debug(
            `[WS] Connection closed cleanly (code=${event.code}, reason=${event.reason || 'none'})`
          );
          updateStatus('disconnected');
        } else {
          console.error(
            `[WS] Connection closed unexpectedly: code=${event.code}, reason=${event.reason || 'none'}, wasClean=${event.wasClean}`
          );
          updateStatus('disconnected');
          handleError(
            new Error(
              `WebSocket closed unexpectedly: code=${event.code}, reason=${event.reason || 'none'}`
            )
          );

          // Attempt reconnection
          const maxAttempts = configRef.current.reconnect?.maxAttempts ?? 10;
          if (configRef.current.reconnect?.enabled && reconnectAttemptsRef.current < maxAttempts) {
            const delay = getReconnectDelay();
            reconnectAttemptsRef.current++;

            console.debug(
              `[WS] Reconnecting in ${delay}ms (attempt ${reconnectAttemptsRef.current}/${maxAttempts})`
            );
            updateStatus('reconnecting');

            reconnectTimeoutRef.current = setTimeout(() => {
              connect();
            }, delay);
          } else if (reconnectAttemptsRef.current >= maxAttempts) {
            console.error(`[WS] Max reconnect attempts (${maxAttempts}) reached, giving up`);
          }
        }
      };

      ws.onerror = () => {
        // Browser WebSocket API doesn't expose error details (no status code or message).
        // The close event that follows will have more info (code + reason).
        console.error('[WS] Connection error (check close event for details)');
        handleError(new Error('WebSocket connection error'));
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data) as WsOutMessage;

          // Handle subscription confirmations (these don't need routing)
          if (message.type === 'Subscribed') {
            // Subscription confirmed, topic is in payload
            return;
          }

          if (message.type === 'Unsubscribed') {
            // Unsubscription confirmed
            return;
          }

          // Handle errors
          if (message.type === 'Error') {
            console.warn('WebSocket error from server:', message.payload);
            return;
          }

          // Route all other messages to handlers
          routeMessage(message);
        } catch (err) {
          console.error('Error parsing WebSocket message:', err);
        }
      };

      wsRef.current = ws;
    } catch (err) {
      console.error('[WS] Failed to create connection:', err);
      handleError(err instanceof Error ? err : new Error('Failed to create WebSocket'));
      updateStatus('error');
    }
  }, [
    updateStatus,
    handleError,
    startHeartbeat,
    stopHeartbeat,
    subscribeToTopic,
    getReconnectDelay,
    routeMessage,
  ]);

  /**
   * Disconnect from WebSocket server
   */
  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    stopHeartbeat();

    if (wsRef.current) {
      const ws = wsRef.current;
      wsRef.current = null;
      // Null handlers before closing so cleanup doesn't trigger reconnect logic
      // or "closed before connection established" warnings in React StrictMode dev
      ws.onopen = null;
      ws.onclose = null;
      ws.onerror = null;
      ws.onmessage = null;
      ws.close(1000, 'Client disconnect');
    }

    updateStatus('disconnected');
    reconnectAttemptsRef.current = 0;
  }, [stopHeartbeat, updateStatus]);

  /**
   * Subscribe to messages on a topic
   *
   * @param topic - Topic string (e.g., 'job:JOB-123', 'org_id:dispatch:route_id')
   * @param handler - Function to handle incoming messages
   * @returns Unsubscribe function
   */
  const subscribe = useCallback(
    <T = WsOutMessage,>(topic: string, handler: MessageHandler<T>): (() => void) => {
      if (!subscriptionsRef.current.has(topic)) {
        subscriptionsRef.current.set(topic, new Set());

        // Subscribe to the topic on the server if connected (skip global '*' topic)
        if (statusRef.current === 'connected' && topic !== '*') {
          subscribeToTopic(topic);
        }
      }

      subscriptionsRef.current.get(topic)!.add(handler as MessageHandler);

      // Return unsubscribe function
      return () => {
        const handlers = subscriptionsRef.current.get(topic);
        if (handlers) {
          handlers.delete(handler as MessageHandler);

          // If no more handlers for this topic, unsubscribe from server (skip global '*' topic)
          if (handlers.size === 0) {
            subscriptionsRef.current.delete(topic);
            if (statusRef.current === 'connected' && topic !== '*') {
              unsubscribeFromTopic(topic);
            }
          }
        }
      };
    },
    [subscribeToTopic, unsubscribeFromTopic]
  );

  /**
   * Unsubscribe all handlers from a topic
   */
  const unsubscribe = useCallback(
    (topic: string) => {
      subscriptionsRef.current.delete(topic);
      // Skip unsubscribing from global '*' topic
      if (statusRef.current === 'connected' && topic !== '*') {
        unsubscribeFromTopic(topic);
      }
    },
    [unsubscribeFromTopic]
  );

  // Refs for connect/disconnect so mount effect fires exactly once
  const connectRef = useRef(connect);
  connectRef.current = connect;
  const disconnectRef = useRef(disconnect);
  disconnectRef.current = disconnect;

  // Connect on mount, disconnect on unmount
  useEffect(() => {
    connectRef.current();

    return () => {
      disconnectRef.current();
    };
  }, []);

  // Retry connection whenever config changes (e.g., auth token becomes available).
  // If already connected, this is a no-op due to the readyState guard in connect().
  // Also clear any pending token-wait retry so we don't double-connect.
  useEffect(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    connectRef.current();
  }, [config]);

  // Resubscribe to all registered topics whenever connection becomes active.
  // This guarantees Subscribe frames are sent regardless of timing between
  // component mount, WS connection, and lazy-loaded features.
  useEffect(() => {
    if (status === 'connected') {
      subscriptionsRef.current.forEach((_, topic) => {
        if (topic !== '*') {
          subscribeToTopic(topic);
        }
      });
    }
  }, [status, subscribeToTopic]);

  const contextValue: WebSocketContextValue = useMemo(
    () => ({
      status,
      isConnected: status === 'connected',
      error: lastError,
      connect,
      disconnect,
      subscribe,
      unsubscribe,
      send,
      subscribedTopics: subscribedTopicsRef.current,
    }),
    [status, lastError, connect, disconnect, subscribe, unsubscribe, send]
  );

  return <WebSocketContext.Provider value={contextValue}>{children}</WebSocketContext.Provider>;
}

/**
 * Hook to access WebSocket context
 *
 * @throws Error if used outside of WebSocketProvider
 *
 * @example
 * ```tsx
 * import { useWebSocket } from '@cfleet/websocket';
 * import { Topics } from '@cfleet/websocket';
 *
 * function JobStatus({ orgId, jobId }: { orgId: string; jobId: string }) {
 *   const { subscribe, status } = useWebSocket();
 *   const [progress, setProgress] = useState(0);
 *
 *   useEffect(() => {
 *     const topic = Topics.orgJob(orgId, jobId);
 *     const unsubscribe = subscribe(topic, (message) => {
 *       if (message.type === 'JobProgress') {
 *         setProgress(message.payload.progress_percent ?? 0);
 *       }
 *     });
 *
 *     return unsubscribe;
 *   }, [orgId, jobId, subscribe]);
 *
 *   return (
 *     <div>
 *       <p>Connection: {status}</p>
 *       <p>Progress: {progress}%</p>
 *     </div>
 *   );
 * }
 * ```
 */
export function useWebSocket(): WebSocketContextValue {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
}
